import { memo, useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { format } from 'date-fns';
import { analyticsActions } from '@actions';
import { AccIntegrationName } from '@constants/integrations';
import { GroupByFilter } from '@constants/analytics';
import { INCLUDE_COVER_MANAGER } from '@constants/feedbacks';
import { subtractYears } from '@helpers/dates';
import { useSurveysOptions, useLocations } from '@hooks';
import DoughnutRates from '@features/Analytics/Common/DoughnutRates';
import SentimentTrend from '@features/Analytics/Common/SentimentTrend';
import { PrintButton } from '@features/Analytics/Common/PrintButton';
import { AnalyticsContainer, HeaderWrapper } from '@features/Analytics/styles';
import AnalyticsWrapper from '@features/Analytics/AnalyticsWrapper';
import FiltersPanel from './FiltersPanel';
import NpsChart from './NpsChart';
import FeedbackTable from './FeedbackTable';
import Questions from './Questions';

const Feedback = memo(() => {
    const firstRender = useRef(true);

    const [filter, setFilter] = useState({
        locations: [],
        surveys: [],
        timeframe: {
            start: subtractYears(new Date(), 1),
            end: new Date(),
        },
        grouping: GroupByFilter.Months,
    });
    const [filterNumber, setFilterNumber] = useState(0);
    const [filterOpened, setFiltersOpened] = useState(false);

    const locations = useSelector(state => state.account?.account?.companies || []);
    const language = useSelector(state => state.auth.user?.language);
    const account = useSelector(state => state.account?.account);
    const { feedbackData, feedbackLoading } = useSelector(state => state.analytics);
    const dispatch = useDispatch();
    const { getSurveysByLocationIds } = useSurveysOptions();
    const { mapLocationsToFormValue } = useLocations();
    const isCoverManagerConnected = account?.integrations.find(
        i => i.type === AccIntegrationName.CoverManager,
    )?.isActive;

    const availableLocations = useMemo(() => locations.filter(location => location?.surveys?.length > 0), [locations]);

    useEffect(() => {
        if (availableLocations.length) {
            const availableSurveys = getSurveysByLocationIds(availableLocations.map(l => l.companyId));
            const surveys = isCoverManagerConnected ? [...availableSurveys, INCLUDE_COVER_MANAGER] : availableSurveys;
            setFilter(prev => ({ ...prev, surveys, locations: mapLocationsToFormValue(availableLocations) }));
        }
    }, [availableLocations, dispatch, isCoverManagerConnected]);

    const currentQuery = useMemo(() => {
        return {
            locations: filter.locations.map(item => item.id),
            surveys: isCoverManagerConnected ? filter.surveys.filter(i => i !== INCLUDE_COVER_MANAGER) : filter.surveys,
            includeCoverManager: isCoverManagerConnected ? filter.surveys.includes(INCLUDE_COVER_MANAGER) : false,
            from: filter.timeframe.start ? format(new Date(filter.timeframe.start), 'yyyy-MM-dd') : null,
            to: filter.timeframe.end ? format(new Date(filter.timeframe.end), 'yyyy-MM-dd') : null,
            grouping: filter.grouping,
        };
    }, [filter, isCoverManagerConnected]);

    useEffect(() => {
        if (filter.locations.length && filter.surveys.length) {
            if (firstRender.current) {
                dispatch(analyticsActions.getAnalyticsFeedback(currentQuery));
                firstRender.current = false;
            }
        }
    }, [filter, dispatch, isCoverManagerConnected]);

    const onApplyFilters = () => {
        dispatch(analyticsActions.getAnalyticsFeedback(currentQuery));
    };

    return (
        <AnalyticsWrapper>
            <HeaderWrapper>
                <FiltersPanel
                    setFiltersOpened={setFiltersOpened}
                    filtersNumber={filterNumber}
                    filterOpened={filterOpened}
                    locations={availableLocations}
                    filter={filter}
                    setFilter={setFilter}
                    type="feedbacks"
                    setFilterNumber={setFilterNumber}
                    onApplyFilters={onApplyFilters}
                />
                <PrintButton />
            </HeaderWrapper>
            <AnalyticsContainer id="pl-content-analytics-feedbacks">
                <DoughnutRates data={feedbackData.overall} type="feedbacks" isLoading={feedbackLoading} />
                <SentimentTrend data={feedbackData.sentimentData} language={language} type="feedbacks" />
                <NpsChart data={feedbackData.npsData} language={language} type="feedbacks" />
                {Object.keys(feedbackData.answersData).length > 0 && (
                    <Questions data={feedbackData.answersData} language={language} />
                )}
                <FeedbackTable
                    data={feedbackData.tableData}
                    isLoading={feedbackLoading}
                    filter={filter}
                    isCoverManagerConnected={isCoverManagerConnected}
                />
            </AnalyticsContainer>
        </AnalyticsWrapper>
    );
});

Feedback.displayName = 'Feedback';

export default Feedback;
